import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { AdminPage, AdminPageHeader, AdminPageTitle, AdminSection } from '../common/AdminPrimitives';
import {
  createStudyCategory,
  deleteStudyCategory,
  getStudyCategoriesList,
  StudyCategory,
  StudyCategoryRequest,
  updateStudyCategory,
} from '../../../services/admin/adminStudyCategoryApi';
import {
  CloseButton,
  FormGroup,
  FormLabel,
  InputField,
  ModalCard,
  ModalOverlay,
  PrimaryButton,
  SecondaryButton,
  SidePanelBody,
  SidePanelFooter,
  SidePanelHeader,
  SidePanelModal,
} from '../common/AdminModalPrimitives';
import {
  Actions,
  AddButton,
  Content,
  DeleteButton,
  EditButton,
  NoData,
  SectionTitle,
  Table,
  TableContainer,
  TableHeader,
} from './StudyManagementStyles';

const DrawerOverlay = styled(ModalOverlay)`
  justify-content: flex-end;
  align-items: stretch;
  padding: 0;
`;

const DrawerTitle = styled.span`
  font-size: 24px;
  font-weight: 700;
  color: #171719;
  letter-spacing: -0.02em;
  line-height: 1.4;
`;

const DrawerFooter = styled(SidePanelFooter)`
  justify-content: space-between;
`;

const DrawerDeleteButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-size: 16px;
  font-weight: 400;
  color: rgba(55, 56, 60, 0.61);
  cursor: pointer;
  line-height: 24px;

  &:hover:not(:disabled) {
    color: rgba(55, 56, 60, 0.88);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const DangerButton = styled(PrimaryButton)`
  background: #e52222;

  &:hover:not(:disabled) {
    background: #d01e1e;
  }

  &:disabled {
    background: rgba(229, 34, 34, 0.5);
  }
`;

const DeleteModalInner = styled.div`
  display: flex;
  flex-direction: column;
  padding: 32px 24px 24px;
  gap: 24px;
`;

const DeleteModalTitle = styled.h3`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: #171719;
  letter-spacing: -0.02em;
`;

const DeleteModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const DeleteModalText = styled.p`
  margin: 0;
  font-size: 16px;
  font-weight: 400;
  color: rgba(55, 56, 60, 0.88);
  line-height: 1.5;
`;

const DeleteModalSelect = styled.select`
  width: 100%;
  padding: 12px;
  border: 1px solid rgba(112, 115, 124, 0.16);
  border-radius: 12px;
  font-size: 16px;
  color: #171719;
  background: #fff;
  outline: none;
  box-sizing: border-box;
  cursor: pointer;

  &:focus {
    border-color: #448181;
  }
`;

const DeleteModalActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const StudyManagementComponent: React.FC = () => {
  const [categories, setCategories] = useState<StudyCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [showDrawer, setShowDrawer] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<StudyCategory | null>(null);
  const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null);
  const [deleteTargetPostCount, setDeleteTargetPostCount] = useState(0);
  const [moveToCategory, setMoveToCategory] = useState('');
  const [formData, setFormData] = useState<StudyCategoryRequest>({
    name: '',
    slug: '',
    sortOrder: 1,
  });

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await getStudyCategoriesList();
      if (response.data) {
        setCategories(response.data);
      }
    } catch {
      alert('카테고리 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const openDrawer = (category?: StudyCategory) => {
    if (category) {
      setEditingCategory(category);
      setFormData({
        name: category.name,
        slug: category.slug,
        sortOrder: category.sort_order,
      });
    } else {
      setEditingCategory(null);
      setFormData({
        name: '',
        slug: '',
        sortOrder: Math.max(...categories.map((c) => c.sort_order), 0) + 1,
      });
    }
    setShowDrawer(true);
  };

  const closeDrawer = () => {
    setShowDrawer(false);
    setEditingCategory(null);
    setFormData({ name: '', slug: '', sortOrder: 1 });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'sortOrder' ? Number(value) : value,
    }));
  };

  const handleSave = async () => {
    if (!formData.name.trim() || !formData.slug.trim()) {
      alert('카테고리명과 슬러그를 입력해주세요.');
      return;
    }
    try {
      setLoading(true);
      if (editingCategory) {
        await updateStudyCategory(editingCategory.id, formData);
      } else {
        await createStudyCategory(formData);
      }
      closeDrawer();
      await fetchCategories();
    } catch {
      alert('카테고리 저장에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const openDeleteModal = (category: StudyCategory) => {
    setDeleteTargetId(category.id);
    setDeleteTargetPostCount(category.post_count);
    setMoveToCategory('');
    setShowDeleteModal(true);
  };

  const openDeleteFromDrawer = () => {
    if (!editingCategory) return;
    const target = editingCategory;
    closeDrawer();
    openDeleteModal(target);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteTargetId(null);
    setDeleteTargetPostCount(0);
    setMoveToCategory('');
  };

  const handleDelete = async () => {
    if (!deleteTargetId) return;
    if (deleteTargetPostCount > 0 && !moveToCategory) {
      alert('게시글을 이동할 카테고리를 선택해주세요.');
      return;
    }
    try {
      setLoading(true);
      await deleteStudyCategory(deleteTargetId, moveToCategory !== '' ? Number(moveToCategory) : undefined);
      closeDeleteModal();
      await fetchCategories();
    } catch {
      alert('카테고리 삭제에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const otherCategories = categories.filter((c) => c.id !== deleteTargetId);

  return (
    <AdminPage>
      <AdminPageHeader>
        <AdminPageTitle>스터디 카테고리 관리</AdminPageTitle>
      </AdminPageHeader>

      <AdminSection>
        <Content>
          <TableHeader>
            <SectionTitle>카테고리</SectionTitle>
            <AddButton onClick={() => openDrawer()} disabled={loading}>
              카테고리 추가
            </AddButton>
          </TableHeader>

          <TableContainer>
            <Table>
              <thead>
                <tr>
                  <th>번호</th>
                  <th>카테고리명</th>
                  <th>슬러그</th>
                  <th>순서</th>
                  <th>게시글 수</th>
                  <th>관리</th>
                </tr>
              </thead>
              <tbody>
                {categories.length === 0 ? (
                  <tr>
                    <NoData colSpan={6}>{loading ? '로딩 중...' : '등록된 카테고리가 없습니다.'}</NoData>
                  </tr>
                ) : (
                  categories.map((category) => (
                    <tr key={category.id}>
                      <td>{category.id}</td>
                      <td>{category.name}</td>
                      <td>{category.slug}</td>
                      <td>{category.sort_order}</td>
                      <td>{category.post_count}</td>
                      <td>
                        <Actions>
                          <EditButton onClick={() => openDrawer(category)} disabled={loading}>
                            수정
                          </EditButton>
                          <DeleteButton onClick={() => openDeleteModal(category)} disabled={loading}>
                            삭제
                          </DeleteButton>
                        </Actions>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </TableContainer>
        </Content>
      </AdminSection>

      {showDrawer && (
        <DrawerOverlay onClick={closeDrawer}>
          <SidePanelModal onClick={(e) => e.stopPropagation()}>
            <SidePanelHeader>
              <DrawerTitle>{editingCategory ? '카테고리 수정' : '카테고리 추가'}</DrawerTitle>
              <CloseButton onClick={closeDrawer}>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M15 5L5 15M5 5l10 10" stroke="#171719" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </CloseButton>
            </SidePanelHeader>

            <SidePanelBody>
              <FormGroup>
                <FormLabel htmlFor="sortOrder">순서</FormLabel>
                <InputField
                  id="sortOrder"
                  type="number"
                  name="sortOrder"
                  value={formData.sortOrder}
                  onChange={handleInputChange}
                  min="1"
                />
              </FormGroup>

              <FormGroup>
                <FormLabel htmlFor="name">카테고리명</FormLabel>
                <InputField
                  id="name"
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="카테고리명을 입력해주세요"
                />
              </FormGroup>

              <FormGroup>
                <FormLabel htmlFor="slug">슬러그</FormLabel>
                <InputField
                  id="slug"
                  type="text"
                  name="slug"
                  value={formData.slug}
                  onChange={handleInputChange}
                  placeholder="ai-basic, data-analysis"
                />
              </FormGroup>
            </SidePanelBody>

            <DrawerFooter>
              {editingCategory ? (
                <DrawerDeleteButton onClick={openDeleteFromDrawer} disabled={loading}>
                  삭제
                </DrawerDeleteButton>
              ) : (
                <span />
              )}
              <PrimaryButton onClick={handleSave} disabled={loading}>
                {loading ? '저장 중...' : '저장하기'}
              </PrimaryButton>
            </DrawerFooter>
          </SidePanelModal>
        </DrawerOverlay>
      )}

      {showDeleteModal && (
        <ModalOverlay onClick={closeDeleteModal}>
          <ModalCard $maxWidth="400px" onClick={(e) => e.stopPropagation()}>
            <DeleteModalInner>
              <DeleteModalTitle>카테고리 삭제</DeleteModalTitle>
              <DeleteModalBody>
                <DeleteModalText>정말로 이 카테고리를 삭제하시겠습니까?</DeleteModalText>
                {deleteTargetPostCount > 0 && (
                  <>
                    <DeleteModalText>
                      이 카테고리에는 게시글 {deleteTargetPostCount}개가 있습니다. 삭제 전에 이동할 카테고리를
                      선택해주세요.
                    </DeleteModalText>
                    <FormGroup>
                      <FormLabel>이동할 카테고리</FormLabel>
                      <DeleteModalSelect
                        value={moveToCategory}
                        onChange={(e) => setMoveToCategory(e.target.value)}
                      >
                        <option value="">카테고리 선택</option>
                        {otherCategories.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                      </DeleteModalSelect>
                    </FormGroup>
                  </>
                )}
              </DeleteModalBody>
              <DeleteModalActions>
                <SecondaryButton onClick={closeDeleteModal} disabled={loading}>
                  취소
                </SecondaryButton>
                <DangerButton
                  onClick={handleDelete}
                  disabled={loading || (deleteTargetPostCount > 0 && moveToCategory === '')}
                >
                  {loading ? '삭제 중...' : '삭제'}
                </DangerButton>
              </DeleteModalActions>
            </DeleteModalInner>
          </ModalCard>
        </ModalOverlay>
      )}
    </AdminPage>
  );
};

export default StudyManagementComponent;
